import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  DiffSourceToggleWrapper,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  Separator,
  StrikeThroughSupSubToggles,
  UndoRedo,
  insertDirective$,
  usePublisher,
} from '@mdxeditor/editor'
import { DIRECTIVES } from '@westhetics/legal'
import type { ContainerDirective } from 'mdast-util-directive'
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * A barra do editor: o básico do Markdown à esquerda, os blocos do dialeto à direita.
 * Nada de cor de texto, tamanho de fonte ou imagem: o documento tem uma tipografia só. Sublinhado
 * e riscado existem desde T40 (pedido do cliente), como `:u[…]` e `~~…~~` do dialeto.
 */

export interface AvailableVariable {
  path: string
  label: string
  example?: string
  /** Grupo do catálogo ("Paciente", "Clínica"); com ele, o menu vem agrupado e ganha busca. */
  group?: string
}

const MENU_WIDTH = 320
const MENU_GUTTER = 16

/**
 * Menu suspenso da barra. A lista sai por um portal em `document.body`, com posição fixa calculada
 * a partir do botão: a barra do MDXEditor rola na horizontal (`overflow-x: auto`) e cortava o menu
 * a 2px de altura, e qualquer contêiner com rolagem acima do editor faria o mesmo.
 */
function Menu({ label, children }: { label: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    const width = Math.min(MENU_WIDTH, window.innerWidth - 2 * MENU_GUTTER)
    setPosition({
      top: rect.bottom + 6,
      left: Math.max(MENU_GUTTER, Math.min(rect.left, window.innerWidth - width - MENU_GUTTER)),
    })
  }, [])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) || listRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    const onScroll = (event: Event) => {
      // Rolar a própria lista não fecha; rolar a página, sim (a posição é fixa).
      if (listRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  const close = useCallback(() => setOpen(false), [])

  return (
    <div className="wx-ed-menu">
      <button
        ref={buttonRef}
        type="button"
        className="wx-ed-menu-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (!open) place()
          setOpen((value) => !value)
        }}
      >
        {label} <span aria-hidden>▾</span>
      </button>
      {open
        ? createPortal(
            <div className="wx-ed-popover">
              <div
                ref={listRef}
                className="wx-ed-menu-list"
                role="menu"
                aria-label={label}
                style={{ top: position.top, left: position.left }}
              >
                {children(close)}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

/**
 * A data do bloco "Cláusula atualizada": hoje em São Paulo, que é a data da publicação para a API
 * (RN-DOC-13). Em UTC, quem edita à noite ganharia a data de amanhã.
 */
function publicationDate() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())
}

type Phrasing = Extract<
  ContainerDirective['children'][number],
  { type: 'paragraph' }
>['children'][number]

/** Uma lista `- **Papel**: texto` (ou `- Papel: texto`) como mdast, para o bloco já nascer no formato. */
function roleList(
  items: Array<[role: string, text: string]>,
  bold: boolean,
): ContainerDirective['children'] {
  return [
    {
      type: 'list',
      ordered: false,
      spread: false,
      children: items.map(([role, text]) => {
        const label: Phrasing = bold
          ? { type: 'strong', children: [{ type: 'text', value: role }] }
          : { type: 'text', value: role }
        return {
          type: 'listItem',
          spread: false,
          children: [
            { type: 'paragraph', children: [label, { type: 'text', value: `: ${text}` }] },
          ],
        }
      }),
    },
  ]
}

/**
 * Partes e Assinaturas nascem com a lista no formato que o dialeto espera (doc 08): quem escreve
 * troca os nomes, não descobre a sintaxe.
 */
const BLOCK_CHILDREN: Record<string, () => ContainerDirective['children']> = {
  parties: () =>
    roleList(
      [
        ['Paciente', '{{patient.name}}, CPF {{patient.docNumber}}'],
        ['Clínica', '{{clinic.legalName}}, {{clinic.idDocument}}'],
      ],
      true,
    ),
  signatures: () =>
    roleList(
      [
        ['Paciente', '{{patient.name}}'],
        ['Profissional responsável', '{{practitioner.name}}'],
      ],
      false,
    ),
}

export function BlocksMenu() {
  const insert = usePublisher(insertDirective$)
  // A galeria (`apiOnly`) fica fora do menu: só a API monta, no plano de tratamento.
  const blocks = DIRECTIVES.filter((d) => d.type === 'containerDirective' && !d.apiOnly)
  return (
    <Menu label="Bloco">
      {(close) => (
        <>
          {blocks.map((d) => (
            <button
              key={d.name}
              type="button"
              role="menuitem"
              onClick={() => {
                const payload = {
                  type: 'containerDirective' as const,
                  name: d.name,
                  attributes: d.name === 'update' ? { date: publicationDate() } : {},
                  children: BLOCK_CHILDREN[d.name]?.() ?? [],
                }
                insert(payload)
                close()
              }}
            >
              <span className="wx-ed-menu-title">{d.title}</span>
              <span className="wx-ed-menu-desc">{d.description}</span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              insert({ type: 'leafDirective', name: 'page-break' })
              close()
            }}
          >
            <span className="wx-ed-menu-title">Quebra de página</span>
            <span className="wx-ed-menu-desc">
              Começa uma página nova no PDF. Sem efeito na tela.
            </span>
          </button>
        </>
      )}
    </Menu>
  )
}

export function VariablesMenu({
  variables,
  insertMarkdown,
}: {
  variables: AvailableVariable[]
  insertMarkdown: (md: string) => void
}) {
  const [query, setQuery] = useState('')
  if (variables.length === 0) return null
  // Com o catálogo inteiro o menu é longo: busca em cima e um título por grupo.
  const searchable = variables.length > 12
  const needle = normalize(query.trim())
  const visible = needle
    ? variables.filter((v) =>
        [v.label, v.path, v.group ?? ''].some((text) => normalize(text).includes(needle)),
      )
    : variables
  return (
    <Menu label="Variável">
      {(close) => (
        <>
          {searchable ? (
            <input
              type="search"
              className="wx-ed-menu-search"
              placeholder="Buscar variável"
              aria-label="Buscar variável"
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
            />
          ) : null}
          {visible.length === 0 ? (
            <div className="wx-ed-menu-empty">Nenhuma variável com esse nome.</div>
          ) : null}
          {visible.map((v, index) => (
            <Fragment key={v.path}>
              {v.group && v.group !== visible[index - 1]?.group ? (
                <div className="wx-ed-menu-group">{v.group}</div>
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  insertMarkdown(`{{${v.path}}}`)
                  setQuery('')
                  close()
                }}
              >
                <span className="wx-ed-menu-title">{v.label}</span>
                <span className="wx-ed-menu-desc">
                  {`{{${v.path}}}`}
                  {v.example ? ` · ex.: ${v.example}` : ''}
                </span>
              </button>
            </Fragment>
          ))}
        </>
      )}
    </Menu>
  )
}

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export function TermButton({ insertMarkdown }: { insertMarkdown: (md: string) => void }) {
  return (
    <button
      type="button"
      className="wx-ed-button"
      title="Termo definido: a palavra com a explicação em linguagem clara"
      onClick={() => insertMarkdown(':term[termo]{def="explicação em linguagem clara"}')}
    >
      Termo definido
    </button>
  )
}

export interface ToolbarProps {
  variables: AvailableVariable[]
  insertMarkdown: (md: string) => void
  /** Sem `previousVersion`, o modo diff não faz sentido e some. */
  withDiff: boolean
}

export function Toolbar({ variables, insertMarkdown, withDiff }: ToolbarProps) {
  const content = (
    <>
      <UndoRedo />
      <Separator />
      <BlockTypeSelect />
      <BoldItalicUnderlineToggles options={['Bold', 'Italic', 'Underline']} />
      <StrikeThroughSupSubToggles options={['Strikethrough']} />
      <ListsToggle options={['bullet', 'number']} />
      <Separator />
      <CreateLink />
      <InsertTable />
      <InsertThematicBreak />
      <Separator />
      <BlocksMenu />
      <VariablesMenu variables={variables} insertMarkdown={insertMarkdown} />
      <TermButton insertMarkdown={insertMarkdown} />
    </>
  )
  return withDiff ? (
    <DiffSourceToggleWrapper>{content}</DiffSourceToggleWrapper>
  ) : (
    <DiffSourceToggleWrapper options={['rich-text', 'source']}>{content}</DiffSourceToggleWrapper>
  )
}
